import { useEffect, useState } from 'react';
import { api } from './client';
import type { Airline } from '@/types/airline';

/**
 * The working carrier list, for a type-ahead.
 *
 * In `lib/` rather than in a feature because more than one screen wants it and a
 * feature may not import from another feature.
 *
 * **`/airline/all` is NOT the whole dictionary**, whatever its name suggests: it
 * returns only the carriers with `kzFlying` set — the working list — while the full
 * ~1035-row dictionary is `/airline/list`. This comment used to claim otherwise.
 *
 * The subset is the right one to offer here: a board is filtered by the carriers this
 * operation actually works with, and it arrives in one response, which is what makes
 * matching in the browser possible without a request per keystroke. A carrier that is
 * in the dictionary but off the working list will not appear in that type-ahead —
 * which is a property of the endpoint, not a fault of the filter.
 */

/**
 * Fetched once per page load and kept.
 *
 * The dictionary changes when someone adds an airline — not while a shift is being
 * worked — so re-fetching per screen would be a request per navigation for data that
 * never moved. A reload picks up a change, which is soon enough.
 */
let cache: Promise<Airline[]> | null = null;

export const fetchAirlines = (): Promise<Airline[]> => {
  cache ??= api
    .get<Airline[]>('/airline/all')
    .then((response) => (Array.isArray(response) ? response : []))
    .catch((caught: unknown) => {
      // Cleared so the next screen can try again: a failed dictionary must not be
      // remembered as an empty one for the rest of the session.
      cache = null;
      throw caught;
    });

  return cache;
};

/**
 * Forgets the cached working list.
 *
 * The cache assumes the dictionary does not move while a shift is worked, which is
 * true until the admin screen moves it — in the same tab. Without this, adding a
 * carrier or putting one on the working list leaves it missing from the board's
 * airline type-ahead until the page is reloaded, which reads as the write not having
 * worked.
 */
export const invalidateAirlines = (): void => {
  cache = null;
};

interface AirlinesState {
  airlines: Airline[];
  isLoading: boolean;
  /** True when the list could not be loaded — the field then behaves as plain text. */
  hasFailed: boolean;
}

/**
 * The airlines, for a type-ahead.
 *
 * A failure here is not fatal: the filter still accepts a typed code, it just cannot
 * be picked from a list. So it reports the failure rather than throwing.
 */
export const useAirlines = (): AirlinesState => {
  const [state, setState] = useState<AirlinesState>(() => ({
    airlines: [],
    isLoading: true,
    hasFailed: false,
  }));

  useEffect(() => {
    let active = true;

    fetchAirlines()
      .then((airlines) => {
        if (active) setState({ airlines, isLoading: false, hasFailed: false });
      })
      .catch(() => {
        if (active) setState({ airlines: [], isLoading: false, hasFailed: true });
      });

    return () => {
      active = false;
    };
  }, []);

  return state;
};

/**
 * The airlines worth offering for what has been typed.
 *
 * Here rather than beside the board's airline filter because the user screen picks
 * airlines the same way, and a feature may not import from another feature.
 *
 * Ranked, not just filtered: a code that STARTS with the query comes first, because
 * someone typing `KC` means KC and not "the airline whose name contains a k and a c".
 * Then codes containing it, then names. An empty query offers the airlines with a
 * code at all, since a row can only ever be filtered by one.
 */
export const rankAirlines = (airlines: readonly Airline[], query: string): Airline[] => {
  const needle = query.trim().toUpperCase();

  const scored = airlines.flatMap((airline) => {
    const code = (airline.iataCode ?? '').toUpperCase();
    const icao = (airline.icaoCode ?? '').toUpperCase();
    const title = (airline.title ?? '').toUpperCase();

    if (code === '') return [];

    if (needle === '') return [{ airline, code, order: 2 }];
    if (code === needle) return [{ airline, code, order: 0 }];
    if (code.startsWith(needle) || icao.startsWith(needle)) return [{ airline, code, order: 1 }];
    if (title.startsWith(needle)) return [{ airline, code, order: 2 }];
    if (title.includes(needle)) return [{ airline, code, order: 3 }];
    if (code.includes(needle) || icao.includes(needle)) return [{ airline, code, order: 4 }];

    return [];
  });

  return scored
    .sort((left, right) =>
      left.order === right.order ? left.code.localeCompare(right.code) : left.order - right.order,
    )
    .map((entry) => entry.airline);
};
